import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNewOrderStore, type NewOrderPayment, type ReceivedMethod } from '@/stores/new-order.store';
import { useAuthStore } from '@/stores/auth.store';
import { api, withSession } from '@/lib/api-client';
import { can } from '@/lib/session';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { money } from '@/lib/format';
import { customerOptions } from '@/lib/options';
import { AddressFields, addressComplete } from '@/components/address-fields';
import { AsyncSelect } from '@/components/async-select';
import { OrderLinesEditor, type PricedLine } from '@/components/order-lines-editor';
import { Choices, ToggleField } from '@/components/fields';
import { AmountRow, DetailLayout, Page, Section } from '@/components/page';
import { Alert, Button, Field, Input, Select, Textarea } from '@/components/ui';
import { itemCount } from './item-count';

/**
 * A new order, written by staff for a customer — a phone or WhatsApp order (ADR-062).
 *
 * Who it is for, what they want, where it goes, how it is paid. The summary is
 * the API's quote for what has been typed so far: prices for this customer
 * (their trade list), the store's campaigns, the coupon, delivery for the
 * address and the cash-on-delivery fee — the same figures the storefront's
 * checkout would show. Nothing is claimed until "Place order".
 *
 * It reads like the order page it becomes: the lines as rows with their
 * pictures, choices as the status panel's radio cards, totals as the order's.
 */

const RECEIVED_METHODS: ReceivedMethod[] = ['BANK_TRANSFER', 'CASH', 'OTHER'];

export function NewOrderPage() {
  const store = useNewOrderStore();
  const user = useAuthStore((state) => state.user);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const navigate = useNavigate();
  const [cardAvailable, setCardAvailable] = useState(false);
  const [showBilling, setShowBilling] = useState(false);

  // A payment link needs card payment switched on and an online provider —
  // the two things checkout itself checks before it takes a card.
  useEffect(() => {
    void Promise.all([withSession(() => api.catalog.paymentMethods()), withSession(() => api.settings.public())])
      .then(([methods, settings]) => {
        setCardAvailable(
          settings['features.flags']?.cardPayment === true && methods.items.some((method) => method.kind !== 'offline'),
        );
      })
      .catch(() => setCardAvailable(false));
  }, []);

  // Priced again a moment after anything that changes the price stops changing.
  const { lines, customerId, address, shippingMethodId, paymentMethod, discountCode, requote } = store;
  useEffect(() => {
    const timer = setTimeout(() => void requote(), 350);
    return () => clearTimeout(timer);
  }, [lines, customerId, address, shippingMethodId, paymentMethod, discountCode, requote, locale]);

  const quote = store.quote;
  const currency = quote?.totals.currency;
  const fmt = (halalas: number) => money(halalas, locale, currency);

  // The quote's prices, matched to the lines by variant — a line is one variant here.
  const priced = useMemo(() => {
    const map = new Map<string, PricedLine>();
    for (const line of store.lines) {
      const answer = quote?.items.find((item) => item.variantId === line.variantId);
      if (answer) map.set(line.key, { unitPrice: answer.unitPrice, lineTotal: answer.lineTotal });
    }
    return map;
  }, [quote, store.lines]);

  const mayRecordPaid = can(user, 'orders:admin');
  const ready =
    store.lines.length > 0 &&
    /\S+@\S+\.\S+/.test(store.email) &&
    addressComplete(store.address) &&
    store.shippingMethodId !== '' &&
    (store.paymentMethod !== 'MANUAL' || store.receivedMethod !== '') &&
    (quote?.issues.length ?? 1) === 0 &&
    !store.quoting;

  const payments: { id: NewOrderPayment; label: MessageKey; hint: MessageKey; enabled: boolean }[] = [
    { id: 'COD', label: 'payCod', hint: 'payCodHint', enabled: true },
    { id: 'MANUAL', label: 'payManual', hint: mayRecordPaid ? 'payManualHint' : 'payManualNeedsAdmin', enabled: mayRecordPaid },
    { id: 'CARD', label: 'payLink', hint: cardAvailable ? 'payLinkHint' : 'payLinkUnavailable', enabled: cardAvailable },
  ];

  return (
    <Page
      title={t('newOrder')}
      subtitle={t('newOrderSubtitle')}
      back={{ to: '/orders', label: t('orders') }}
      actions={
        <Button variant="secondary" onClick={() => store.reset()} disabled={store.placing}>
          {t('discardDraft')}
        </Button>
      }
      width="wide"
    >
      {store.error ? <Alert>{store.error}</Alert> : null}

      <DetailLayout
        stickyAside
        main={
          <>
            <Section title={t('customer')} subtitle={t('newOrderCustomerHint')}>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('customerAccount')} htmlFor="new-customer">
                  <AsyncSelect
                    id="new-customer"
                    value={store.customerId}
                    emptyLabel={t('guestNoAccount')}
                    fetchPage={(search, cursor) => customerOptions.page(search, cursor)}
                    fetchOne={(id) => customerOptions.one(id)}
                    onChange={(id) => void store.chooseCustomer(id)}
                  />
                </Field>
                <Field label={t('email')} htmlFor="new-email" hint={t('newOrderEmailHint')}>
                  <Input
                    id="new-email"
                    type="email"
                    dir="ltr"
                    value={store.email}
                    onChange={(event) => store.set({ email: event.target.value })}
                  />
                </Field>
              </div>
            </Section>

            <Section
              title={t('items')}
              subtitle={
                store.lines.length > 0
                  ? itemCount(store.lines.reduce((sum, line) => sum + line.quantity, 0), t, locale)
                  : undefined
              }
            >
              <OrderLinesEditor
                lines={store.lines}
                priced={priced}
                currency={currency}
                onChange={(next) => store.set({ lines: next })}
              />
            </Section>

            <Section title={t('deliveryAddress')}>
              <AddressFields value={store.address} onChange={(next) => store.set({ address: next })} idPrefix="new-address" />
              {store.customerId ? (
                <div className="mt-4">
                  <ToggleField
                    label={t('saveToAddressBook')}
                    checked={store.saveAddress}
                    onChange={(checked) => store.set({ saveAddress: checked })}
                  />
                </div>
              ) : null}
            </Section>

            <Section title={t('delivery')}>
              {quote && quote.shippingOptions.length > 0 ? (
                <Choices
                  legend={t('deliveryMethod')}
                  name="new-order-delivery"
                  columns={2}
                  legendHidden
                  options={quote.shippingOptions.map((option) => ({
                    value: option.methodId,
                    label: option.name,
                    hint: option.isPickup ? t('pickupAtStore') : undefined,
                    aside: (
                      <span data-numeric>{option.price === 0 ? t('free') : fmt(option.price)}</span>
                    ),
                  }))}
                  value={store.shippingMethodId || null}
                  onChange={(methodId) => store.set({ shippingMethodId: methodId })}
                />
              ) : (
                <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">{t('deliveryNeedsAddress')}</p>
              )}
            </Section>

            <Section title={t('orderPayment')}>
              <div className="flex flex-col gap-4">
                <Choices
                  legend={t('paymentMethod')}
                  name="new-order-payment"
                  columns={3}
                  legendHidden
                  options={payments.map((option) => ({
                    value: option.id,
                    label: t(option.label),
                    hint: t(option.hint),
                    disabled: !option.enabled,
                  }))}
                  value={store.paymentMethod}
                  onChange={(method) => store.set({ paymentMethod: method })}
                />
                {store.paymentMethod === 'MANUAL' ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={t('receivedBy')} htmlFor="new-received">
                      <Select
                        id="new-received"
                        className="w-full"
                        value={store.receivedMethod}
                        onChange={(event) => store.set({ receivedMethod: event.target.value as ReceivedMethod | '' })}
                      >
                        <option value="">{t('dispositionPick')}</option>
                        {RECEIVED_METHODS.map((method) => (
                          <option key={method} value={method}>
                            {t(`refundMethod${method}` as MessageKey)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('refundReference')} htmlFor="new-received-ref" hint={t('receivedReferenceHint')}>
                      <Input
                        id="new-received-ref"
                        dir="ltr"
                        maxLength={120}
                        value={store.receivedReference}
                        onChange={(event) => store.set({ receivedReference: event.target.value })}
                      />
                    </Field>
                  </div>
                ) : null}
              </div>
            </Section>

            <Section title={t('notesAndBilling')}>
              <div className="flex flex-col gap-4">
                <Field label={t('customerNote')} htmlFor="new-note" hint={t('customerNoteHint')}>
                  <Textarea
                    id="new-note"
                    rows={2}
                    maxLength={500}
                    value={store.note}
                    onChange={(event) => store.set({ note: event.target.value })}
                  />
                </Field>
                <Field label={t('staffNoteOnOrder')} htmlFor="new-staff-note" hint={t('orderNotesStaffOnly')}>
                  <Textarea
                    id="new-staff-note"
                    rows={2}
                    maxLength={1000}
                    placeholder={t('orderNotePlaceholder')}
                    value={store.staffNote}
                    onChange={(event) => store.set({ staffNote: event.target.value })}
                  />
                </Field>
                {showBilling || store.billing ? (
                  <BillingFields
                    value={store.billing}
                    onChange={(billing) => store.set({ billing })}
                    onRemove={() => {
                      store.set({ billing: null });
                      setShowBilling(false);
                    }}
                  />
                ) : (
                  <div>
                    <Button variant="secondary" size="sm" onClick={() => setShowBilling(true)}>
                      {t('billABusiness')}
                    </Button>
                  </div>
                )}
                <ToggleField
                  label={t('sendConfirmation')}
                  hint={t('sendConfirmationHint')}
                  checked={store.notifyCustomer}
                  onChange={(checked) => store.set({ notifyCustomer: checked })}
                />
              </div>
            </Section>
          </>
        }
        aside={
          <Section title={t('orderSummary')} subtitle={store.quoting ? t('loading') : undefined}>
            {quote ? (
              // The order page's totals, line for line — what this order will show once placed.
              <dl className="flex flex-col gap-2 text-sm">
                <AmountRow label={t('subtotal')} value={fmt(quote.totals.subtotal)} />
                {quote.totals.promotionTotal > 0 ? (
                  <AmountRow label={t('promotionSavings')} value={`−${fmt(quote.totals.promotionTotal)}`} />
                ) : null}
                {quote.totals.discountTotal > 0 ? (
                  <AmountRow
                    label={quote.discount ? `${t('discount')} (${quote.discount.code})` : t('discount')}
                    value={`−${fmt(quote.totals.discountTotal)}`}
                  />
                ) : null}
                <AmountRow label={t('shipping')} value={fmt(quote.totals.shippingTotal)} />
                {quote.totals.paymentFee > 0 ? <AmountRow label={t('paymentFee')} value={fmt(quote.totals.paymentFee)} /> : null}
                <AmountRow label={t('total')} value={fmt(quote.totals.grandTotal)} emphasis />
                <p className="text-xs text-ink-muted">{t('vatIncluded', { amount: fmt(quote.totals.taxTotal) })}</p>
              </dl>
            ) : (
              <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">{t('noLinesYet')}</p>
            )}

            <div className="mt-4">
              <Field label={t('discountCode')} htmlFor="new-coupon" hint={t('optional')}>
                <Input
                  id="new-coupon"
                  dir="ltr"
                  maxLength={40}
                  value={store.discountCode}
                  onChange={(event) => store.set({ discountCode: event.target.value.toUpperCase() })}
                />
              </Field>
            </div>

            {quote && quote.issues.length > 0 ? (
              // What stops it being placed, in the API's words.
              <ul className="mt-4 flex flex-col gap-1 rounded-control bg-danger/5 px-3 py-2">
                {quote.issues.map((issue) => (
                  <li key={issue.code} className="text-xs text-danger">
                    {issue.message}
                  </li>
                ))}
              </ul>
            ) : null}

            <Button
              className="mt-4 w-full"
              disabled={!ready || store.placing}
              onClick={async () => {
                const placed = await store.place();
                if (placed) navigate(`/orders/${placed.id}`);
              }}
            >
              {store.placing ? t('saving') : quote ? t('placeOrderFor', { amount: fmt(quote.totals.grandTotal) }) : t('placeOrder')}
            </Button>
            {store.paymentMethod === 'CARD' ? (
              <p className="mt-3 rounded-control bg-surface-sunken px-3 py-2 text-xs text-ink-muted">{t('payLinkAfter')}</p>
            ) : null}
            {/* A refusal lands beside the button that caused it, not only at the top of a long page. */}
            {store.error ? (
              <div className="mt-3">
                <Alert>{store.error}</Alert>
              </div>
            ) : null}
          </Section>
        }
      />
    </Page>
  );
}

/** Who the invoice is addressed to, when it is not the recipient (R5). */
export function BillingFields({
  value,
  onChange,
  onRemove,
}: {
  value: { name: string; companyName?: string; line1?: string; city?: string; vatNumber?: string } | null;
  onChange: (next: { name: string; companyName?: string; line1?: string; city?: string; vatNumber?: string }) => void;
  onRemove: () => void;
}) {
  const t = useT();
  const current = value ?? { name: '' };
  const set = (patch: Partial<typeof current>) => onChange({ ...current, ...patch });
  return (
    <div className="flex flex-col gap-3 rounded-control border border-line p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink">{t('billedTo')}</span>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          {t('remove')}
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('fullName')} htmlFor="billing-name">
          <Input id="billing-name" value={current.name} onChange={(event) => set({ name: event.target.value })} />
        </Field>
        <Field label={t('company')} htmlFor="billing-company" hint={t('optional')}>
          <Input id="billing-company" value={current.companyName ?? ''} onChange={(event) => set({ companyName: event.target.value })} />
        </Field>
        <Field label={t('addressLine1')} htmlFor="billing-line1" hint={t('optional')}>
          <Input id="billing-line1" value={current.line1 ?? ''} onChange={(event) => set({ line1: event.target.value })} />
        </Field>
        <Field label={t('city')} htmlFor="billing-city" hint={t('optional')}>
          <Input id="billing-city" value={current.city ?? ''} onChange={(event) => set({ city: event.target.value })} />
        </Field>
        <Field label={t('vatNumber')} htmlFor="billing-vat" hint={t('vatNumberHint')}>
          <Input id="billing-vat" dir="ltr" value={current.vatNumber ?? ''} onChange={(event) => set({ vatNumber: event.target.value })} />
        </Field>
      </div>
    </div>
  );
}
